import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPopper, Instance } from "@popperjs/core";
import {
  Bell,
  ChevronLeft,
  ChevronRight,
  Check,
  Trash2,
  X,
} from "lucide-react";
import accessURL from "../utils/access";

interface NotificationData {
  id: number;
  message: string;
  type: string;
  is_read: number;
  created_at: string;
  updatedAt: string;
}

interface PaginatedResponse {
  current_page: number;
  per_page: number;
  total_items: number;
  unread_items: string;
  total_pages: number;
  data: NotificationData[];
}

interface ApiResponse {
  status: string;
  code: number;
  data: {
    message: string;
  };
}

const Notify: React.FC = () => {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [isBouncing, setIsBouncing] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] = useState<{
    id: number;
    message: string;
  } | null>(null);

  const notificationRef = useRef<HTMLButtonElement>(null);
  const notificationBox = useRef<HTMLDivElement>(null);
  const [popperInstance, setPopperInstance] = useState<Instance | null>(null);

  // Add bounce animation every 20 minutes if there are unread notifications
  useEffect(() => {
    if (unreadCount > 0) {
      const bounceInterval = setInterval(() => {
        setIsBouncing(true);
        setTimeout(() => setIsBouncing(false), 1000);
      }, 1200000); // 20 minutes

      return () => clearInterval(bounceInterval);
    }
  }, [unreadCount]);

  const handleMarkAsRead = async (id: number) => {
    const response = await accessURL.post(`messages/${id}`);
    if (response) {
      window.location.reload();
    }
  };

  const handleNotificationClick = async (id: number) => {
    try {
      const response = await accessURL.get<ApiResponse>(
        `messages/singleMessages/${id}`
      );
      setSelectedNotification({
        id,
        message: response.data.data.message,
      });
    } catch (error) {
      console.error("Error fetching notification details:", error);
    }
  };

  const handleCloseNotificationModal = async () => {
    if (selectedNotification) {
      await handleMarkAsRead(selectedNotification.id);
      setSelectedNotification(null);
    }
  };

  const handleDelete = async (id: number) => {
    const response = await accessURL.post(`messages/delete/${id}`);
    if (response) {
      window.location.reload();
    }
  };

  const fetchNotifications = async (page: number = 1): Promise<void> => {
    setLoading(true);
    try {
      const response = await accessURL.get<{
        status: string;
        code: number;
        data: PaginatedResponse;
      }>(`messages?page=${page}`);
      const { data } = response.data;
      setNotifications(data.data);
      setUnreadCount(parseInt(data.unread_items));
      setTotalPages(data.total_pages);
      setCurrentPage(data.current_page);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const truncateMessage = (message: string): string => {
    const words = message.split(" ").slice(0, 3).join(" ");
    return words + (message.split(" ").length > 3 ? "..." : "");
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const showNotificationBox = useCallback(() => {
    if (popperInstance || !notificationRef.current || !notificationBox.current)
      return;

    const instance = createPopper(
      notificationRef.current,
      notificationBox.current,
      {
        placement: "bottom-end",
        modifiers: [
          {
            name: "offset",
            options: { offset: [0, 10] },
          },
          {
            name: "preventOverflow",
            options: { altAxis: true },
          },
        ],
      }
    );

    setPopperInstance(instance);
  }, [popperInstance]);

  const hideNotificationBox = useCallback(() => {
    if (popperInstance) {
      popperInstance.destroy();
      setPopperInstance(null);
    }
  }, [popperInstance]);

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    if (showNotification) {
      fetchNotifications(currentPage);
    }
  }, [currentPage, showNotification]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node) &&
        notificationBox.current &&
        !notificationBox.current.contains(event.target as Node)
      ) {
        hideNotificationBox();
        setShowNotification(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [hideNotificationBox]);

  useEffect(() => {
    if (showNotification) {
      showNotificationBox();
      notificationRef.current?.setAttribute("aria-expanded", "true");
    } else {
      hideNotificationBox();
      notificationRef.current?.setAttribute("aria-expanded", "false");
    }
  }, [showNotification, showNotificationBox, hideNotificationBox]);

  return (
    <>
      <div className="relative">
        <button
          ref={notificationRef}
          className={`relative h-10 w-10 rounded-full p-0 bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-gray-900 ${
            isBouncing ? "animate-bounce" : ""
          }`}
          onClick={() => setShowNotification(!showNotification)}
          aria-expanded={showNotification}
        >
          <Bell className="h-6 w-6 text-gray-300 mx-auto" />
          {unreadCount > 0 && (
            <span className="absolute -top-[0.2px] -right-[0.2px] flex h-5 w-5 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-xs font-bold text-white z-50">
                {unreadCount}
              </span>
            </span>
          )}
        </button>

        {showNotification && (
          <div
            ref={notificationBox}
            className="fixed right-4 top-16 z-50 w-96 sm:absolute sm:right-0 sm:top-full sm:mt-1"
          >
            <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
              <div className="border-b border-gray-700 bg-gray-800 p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-200">
                    Notifications
                  </h3>
                  <div className="flex items-center gap-2">
                    {unreadCount > 0 && (
                      <span className="rounded-full bg-primary px-2 py-1 text-xs font-bold text-white">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="max-h-[calc(100vh-16rem)] overflow-y-auto p-2 bg-gray-900">
                {loading ? (
                  <div className="flex h-32 items-center justify-center">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="flex h-32 items-center justify-center text-gray-400">
                    No notifications
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className="rounded-lg border border-gray-700 bg-gray-800 p-3 hover:bg-gray-700 transition-colors duration-200"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() =>
                              handleNotificationClick(notification.id)
                            }
                          >
                            <p className="mt-1 text-sm text-gray-400">
                              {truncateMessage(notification.message)}
                            </p>
                            <p className="mt-2 text-xs text-gray-500">
                              {formatDate(notification.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {notification.is_read === 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMarkAsRead(notification.id);
                                }}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md transition-colors duration-200 flex items-center gap-1.5"
                                title="Mark as read"
                              >
                                <Check className="h-4 w-4" />
                                <span>Mark as read</span>
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(notification.id);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-500 transition-colors duration-200"
                              title="Delete notification"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="border-t border-gray-700 bg-gray-800 p-2">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1 text-gray-400 hover:text-gray-200 disabled:opacity-50 transition-colors duration-200"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="p-1 text-gray-400 hover:text-gray-200 disabled:opacity-50 transition-colors duration-200"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Notification Details Modal */}
      {selectedNotification && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center  backdrop-blur-sm">
          <div className="relative w-full max-w-md rounded-lg bg-gray-800 p-6 shadow-xl">
            <button
              onClick={handleCloseNotificationModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
            <h2 className="mb-4 text-xl font-semibold text-white">
              Notification Details
            </h2>
            <p className="text-gray-300">{selectedNotification.message}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Notify;
